import { currencyFieldManagerConfigDefaults } from './types.js';
const separatorPattern = /(\d)(?=(\d\d\d)+(?!\d))/g;
/**
 * Create a currency field formatter for a given configuration. This function returns an instance of a CurrencyFormatter
 * which takes a string and will return that string to match the given currency format
 * @param config {Partial<CurrencyFieldManagerConfig>} - The configuration for the currency field manager
 * @returns {CurrencyFormatter} - A currency formatter function `(value: string) => string`
 */
export function createFormatter(config = currencyFieldManagerConfigDefaults) {
    const realizedConfig = {
        ...currencyFieldManagerConfigDefaults,
        ...config
    };
    const { decimalSeparator, currencySign, currencySignPosition, maximumFractionDigits, minimumDecimalPrecision, thousandsSeparator } = realizedConfig;
    const rawPattern = `[^\\d\\${decimalSeparator}]`;
    const invalidCharacterPattern = new RegExp(rawPattern, 'gi');
    return function formatter(value) {
        const number = value.replace(invalidCharacterPattern, '');
        const [dollars, ...centsArray] = number.split(decimalSeparator);
        const dollarsFormatted = dollars.replace(separatorPattern, `$1${thousandsSeparator}`);
        let formatted;
        if (centsArray.length === 0) {
            if (currencySignPosition === 'start') {
                formatted = `${currencySign}${dollarsFormatted}`;
            }
            else {
                formatted = `${dollarsFormatted}${currencySign}`;
            }
        }
        else {
            /** If cents is defined */
            const cents = centsArray.join('');
            if (currencySignPosition === 'start') {
                formatted = `${currencySign}${dollarsFormatted}${decimalSeparator}${cents.substring(0, maximumFractionDigits)}`;
            }
            else {
                formatted = `${dollarsFormatted}${decimalSeparator}${cents.substring(0, maximumFractionDigits)}${currencySign}`;
            }
            /** Remove decimalSeparator from value
             *  $100. to $100
             */
            if (cents.length === 0 && minimumDecimalPrecision <= 0) {
                formatted = formatted.replace(decimalSeparator, '');
            }
            /** Remove decimalSeparator from value
             *  $100. to $100
             */
            if (maximumFractionDigits <= 0) {
                formatted = formatted.replace(decimalSeparator, '');
            }
            /** Add padding to decimal if minimumDecimalPrecision and maximumFractionDigits allow for it */
            if (maximumFractionDigits > 0 && minimumDecimalPrecision > 0) {
                const [formattedValue, decimalValue] = formatted.split(decimalSeparator);
                formatted = `${formattedValue}${decimalSeparator}${decimalValue.padEnd(minimumDecimalPrecision, '0')}`;
            }
        }
        return formatted;
    };
}
/**
 * Determines whether the key pressed in a KeyboardEvent is a number key.
 *
 * @param event the KeyboardEvent to be checked
 * @returns whether the key pressed in the KeyboardEvent is a number key
 */
export function isNumberKey(event) {
    return !isNaN(Number(event.key));
}
//# sourceMappingURL=currency-field-utilities.js.map